// Shared helper for combining the Terms & Conditions template library
// into the flat `terms_snapshot` array saved on a document (Sales
// Order / Quotation / Purchase Order). Paired with
// `TermsTemplatePicker.jsx` so Raise Transfer Order, Quotations and
// Purchase Orders all combine "selected templates + inline edits +
// one-off extra text" the exact same way instead of each re-writing
// this logic.
//
// Precedence:
//   1. One or more templates selected → each becomes an entry (using
//      any inline override text in place of the template's saved body).
//   2. Nothing selected → falls back to the company's Settings →
//      default_terms text (with its own inline override, if edited).
//   3. Either way, free-form "extra / one-off" text — typed on top of
//      the selection for this document only — is appended as a final
//      entry when present.
//
// Returns `null` when there is nothing to save (matches the previous
// per-page behavior so documents without any T&C stay unset).
export function buildTermsSnapshot({ templates, selectedIds, overrides, defaultText, defaultOverride, extraText }) {
  const selected = (selectedIds || [])
    .map((id) => (templates || []).find((t) => t.id === id))
    .filter(Boolean);

  const entries = [];

  if (selected.length > 0) {
    entries.push(
      ...selected.map((t) => ({
        id: t.id,
        name: t.name,
        body: overrides?.[t.id] ?? (t.body || t.content || ""),
      }))
    );
  } else if (defaultText) {
    entries.push({
      id: null,
      name: "Terms & Conditions",
      body: defaultOverride ?? defaultText,
    });
  }

  const extra = (extraText || "").trim();
  if (extra) {
    entries.push({ id: null, name: "Additional Notes", body: extra });
  }

  return entries.length ? entries : null;
}

// Re-hydrate a previously-saved `terms_snapshot` back into the
// `{ overrides, defaultOverride, extraText }` shape the picker/form use,
// so re-opening a draft (e.g. editing a Quotation) shows the same
// inline edits again instead of reverting to the raw template text.
export function splitTermsSnapshot(snapshot) {
  const overrides = {};
  let defaultOverride;
  let extraText = "";
  if (Array.isArray(snapshot)) {
    for (const t of snapshot) {
      if (t.id) overrides[t.id] = t.body;
      else if (t.name === "Additional Notes") extraText = t.body || "";
      else defaultOverride = t.body;
    }
  }
  return { overrides, defaultOverride, extraText };
}
